import { useState } from "react";
import { useRecords } from "../../../api/useRecords";
import { useNotesRecords } from "../../../api/useNotesRecords";
import { useRelatedRecords } from "../../../api/useRelatedRecords";
import { buildTimelineEntries } from "./buildTimeline";
import type { LogbookSessionItem, LogbookTaskTimelineItem, LogbookCustomerSurveyItem } from "../types";

export const useLogbookData = (treatmentId: string | number | undefined, sessions: Array<LogbookSessionItem>, noteFilter: any) => {
	const [showOrders, setShowOrders] = useState(false);
	const [serviceFilter, setServiceFilter] = useState<Array<number>>([]);
	const [statusFilter, setStatusFilter] = useState<Array<string> | null>(null);
	const [taskFilter, setTaskFilter] = useState<string>("all");
	const [showFilters, setShowFilters] = useState(false);

	const notesQueryFilter = noteFilter
		? { _and: [{ treatments: { id: { _eq: treatmentId } } }, noteFilter] }
		: { treatments: { id: { _eq: treatmentId } } };

	const { data: notesData } = useNotesRecords({
		fields: "id,content,date_created,category,title,sessions",
		filter: notesQueryFilter,
	});

	const { data: prescriptionData } = useRecords("treatment_prescription",
		"id,date_created,user_created.first_name,user_created.last_name,user_created.avatar,services_prescribed.service.id,services_prescribed.service.name,services_prescribed.estimated_total_sessions",
		{ extraOptions: { filter: { treatment: { _eq: treatmentId } } } }
	);

	const { data: ordersData } = useRecords("orders",
		"id,order_date,payment_status,payment_date,order_items.service_variant.service.name,order_items.quantity,order_items.price,order_items.discount",
		{ extraOptions: { filter: { treatment: { _eq: treatmentId } } } }
	);

	const { data: tasksData } = useRecords("tasks",
		"id,content,status,date_created",
		{ extraOptions: { filter: { related_with: { "item:treatments": { id: { _eq: treatmentId } } } } } }
	);

	const { data: customerSurveysData } = useRelatedRecords("customer_surveys", "*.*,treatment.client.*.*", "treatment", treatmentId);

	const notes = notesData ?? [];
	const prescriptions = prescriptionData?.items ?? [];
	const orders = ordersData?.items ?? [];
	const tasks: Array<LogbookTaskTimelineItem> = tasksData?.items ?? [];
	const customerSurveys: Array<LogbookCustomerSurveyItem> = customerSurveysData?.items ?? [];
	const defaultStatuses = [...new Set(sessions.map((session) => session.status).filter(Boolean))] as Array<string>;
	const activeStatusFilter = statusFilter ?? defaultStatuses.filter((s) => s !== "booked");

	const serviceFilteredSessions = serviceFilter.length > 0
		? sessions.filter((session) => session.service?.id != null && serviceFilter.includes(session.service.id))
		: sessions;
	const filteredSessions = activeStatusFilter.length > 0
		? serviceFilteredSessions.filter((session) => activeStatusFilter.includes(session.status))
		: serviceFilteredSessions;

	const filteredOrders = showOrders ? orders : [];

	const filteredTasks = taskFilter === "hide" ? []
		: taskFilter === "open" ? tasks.filter((task) => task.status === "open")
		: taskFilter === "done" ? tasks.filter((task) => task.status === "done")
		: tasks;

	const filtersActivated = serviceFilter.length > 0 || statusFilter !== null || showOrders || taskFilter !== "all";

	const timeline = buildTimelineEntries(filteredSessions, notes, prescriptions, filteredOrders, filteredTasks, customerSurveys);

	return {
		timeline, filtersActivated, showFilters, setShowFilters,
		filterState: { serviceFilter, setServiceFilter, activeStatusFilter, setStatusFilter, showOrders, setShowOrders, taskFilter, setTaskFilter },
	};
};
